import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewContainerRef,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { ofType, Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';

import { filterBoolean, QueryFilterPipe } from '@recruitee/common';
import { GdprService } from '@recruitee/gdpr-data';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { JobsActions, StatusChangeType, JobItemsSelectors } from '@recruitee/offers-data';
import type { OfferItem } from '@recruitee/offers-types';
import { OfferStatus } from '@recruitee/offers-types';
import {
  DialogRef,
  DialogService,
  NotificationsController,
  DialogFooterComponent,
  EmptyStateComponent,
  DividerComponent,
  ButtonComponent,
  HeadingComponent,
  OfferStatusIndicatorComponent,
  GroupComponent,
  IconComponent,
  IconColorDirective,
  InputComponent,
  FormFieldComponent,
  BoxComponent,
  DialogBodyComponent,
  DialogHeaderComponent,
} from '@recruitee/user-interface';

@Component({
  selector: 'rt-offers-archive-dialog',
  templateUrl: './offers-archive-dialog.component.html',
  styleUrls: ['./offers-archive-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    BoxComponent,
    FormFieldComponent,
    InputComponent,
    ReactiveFormsModule,
    IconColorDirective,
    IconComponent,
    GroupComponent,
    OfferStatusIndicatorComponent,
    HeadingComponent,
    ButtonComponent,
    DividerComponent,
    EmptyStateComponent,
    DialogFooterComponent,
    TranslatePipe,
    QueryFilterPipe,
  ],
})
export class OffersArchiveDialogComponent {
  private readonly translations = {
    confirm_message: this.gdprService.isGdprEnabled()
      ? this.i18n.translate('app.offers.actions.archive.confirm_message.job_gdpr')
      : this.i18n.translate('app.offers.actions.archive.confirm_message.job'),
    confirm_title: this.i18n.translate('app.offers.actions.archive.confirm_title.job'),

    success_message: this.i18n.translate('app.offers.actions.archive.success_message.job'),
  };

  public jobs: OfferItem[] = [];
  public searchControl = new FormControl<string>('', { nonNullable: true });
  private ngOnOnDestroy$ = new Subject<void>();

  constructor(
    private dialog: DialogRef,
    private dialogService: DialogService,
    private gdprService: GdprService,
    private notificationsCtrl: NotificationsController,
    private i18n: I18nService,
    private viewContainerRef: ViewContainerRef,
    protected cdRef: ChangeDetectorRef,
    private store: Store,
    private actions$: Actions,
  ) {}

  public ngOnInit(): void {
    this.store
      .select(JobItemsSelectors.active)
      .pipe(filterBoolean, takeUntil(this.ngOnOnDestroy$.asObservable()))
      .subscribe(offers => {
        this.jobs = offers.filter(offer => offer.status !== OfferStatus.Draft);
        this.cdRef.detectChanges();
      });

    this.searchControl.valueChanges
      .pipe(takeUntil(this.ngOnOnDestroy$.asObservable()))
      .subscribe(() => {
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnOnDestroy$.next();
    this.ngOnOnDestroy$.complete();
  }

  public isSearching(): boolean {
    return !!this.searchControl.value && this.searchControl.value.length > 0;
  }

  public cancelSearch(): void {
    this.searchControl.reset('');
  }

  public close(): void {
    this.dialog.close();
  }

  public archiveOffer(offer: OfferItem): void {
    this.dialogService
      .confirm(
        {
          title: this.translations.confirm_title,
          message: this.translations.confirm_message,
          variant: 'information',
          isCancelable: true,
        },
        this.viewContainerRef,
      )
      .pipe(filterBoolean, takeUntil(this.ngOnOnDestroy$.asObservable()))
      .subscribe(confirmed => {
        this.store.dispatch(
          JobsActions.changeStatus({
            payload: { id: offer.id, status: StatusChangeType.Archive },
          }),
        );

        this.actions$
          .pipe(
            ofType(JobsActions.changeStatusSuccess),
            take(1),
            takeUntil(this.ngOnOnDestroy$.asObservable()),
          )
          .subscribe(() => {
            this.notificationsCtrl.success(this.translations.success_message);
          });
      });
  }

  public trackByOffer<T extends { id: number }>(index: number, offer: T): number {
    return offer.id;
  }

  public trackByIndex<T>(index: number, value: T): number {
    return index;
  }
}
